import React from 'react';
import {useTranslation} from 'react-i18next';
import {useLivenessStore} from './store';
import {
  InstructionsContainer,
  InstructionText,
  LottieHelperIcon,
  LottieStatusIcon,
} from './styled';

type LivenessInstructionsProps = {
  className?: string;
};

export const LivenessInstructions: React.FC<LivenessInstructionsProps> = ({
  className,
}) => {
  const {t} = useTranslation('identity-verification');
  const currentStep = useLivenessStore(state => state.currentStep);
  const stepStatus = useLivenessStore(state => state.stepStatus);
  const [lotties, setLotties] = React.useState<{
    success: object | null;
    error: object | null;
    front: object | null;
    up: object | null;
    blink: object | null;
    side: object | null;
  }>({
    success: null,
    error: null,
    front: null,
    up: null,
    blink: null,
    side: null,
  });

  React.useEffect(() => {
    let isMounted = true;

    Promise.allSettled([
      import('./icons/Success.json'),
      import('./icons/Error.json'),
      import('./icons/Liveness_Front.json'),
      import('./icons/Liveness_Up.json'),
      import('./icons/Liveness_Blink.json'),
      import('./icons/Liveness_Side.json'),
    ]).then(
      ([successResult, errorResult, frontResult, upResult, blinkResult, sideResult]) => {
        if (!isMounted) {
          return;
        }

        setLotties({
          success:
            successResult.status === 'fulfilled' ? successResult.value.default : null,
          error: errorResult.status === 'fulfilled' ? errorResult.value.default : null,
          front: frontResult.status === 'fulfilled' ? frontResult.value.default : null,
          up: upResult.status === 'fulfilled' ? upResult.value.default : null,
          blink: blinkResult.status === 'fulfilled' ? blinkResult.value.default : null,
          side: sideResult.status === 'fulfilled' ? sideResult.value.default : null,
        });
      },
    );

    return () => {
      isMounted = false;
    };
  }, []);

  const getInstructionText = (): string => {
    switch (currentStep) {
      case 'LOOK_STRAIGHT':
        return t('liveness_detection.instructions.look_straight');
      case 'TURN_LEFT':
        return t('liveness_detection.instructions.turn_left');
      case 'TURN_RIGHT':
        return t('liveness_detection.instructions.turn_right');
      case 'TURN_UP':
        return t('liveness_detection.instructions.turn_up');
      case 'BLINK':
        return t('liveness_detection.instructions.blink');
      case 'COMPLETED':
        return t('liveness_detection.instructions.completed');
      default:
        return '';
    }
  };

  const getIcon = () => {
    const {success, error, front, up, blink, side} = lotties;

    if (!success || !error || !front || !up || !blink || !side) {
      return null;
    }

    if (stepStatus === 'error') {
      return <LottieStatusIcon animationData={error} />;
    }
    if (stepStatus === 'success') {
      return <LottieStatusIcon loop={false} animationData={success} />;
    }

    switch (currentStep) {
      case 'LOOK_STRAIGHT':
        return <LottieHelperIcon loop animationData={front} />;
      case 'TURN_LEFT':
        return (
          <div style={{transform: 'scaleX(-1)'}}>
            <LottieHelperIcon loop animationData={side} />
          </div>
        );
      case 'TURN_RIGHT':
        return <LottieHelperIcon loop animationData={side} />;
      case 'TURN_UP':
        return <LottieHelperIcon loop animationData={up} />;
      case 'BLINK':
        return <LottieHelperIcon loop animationData={blink} />;
      case 'COMPLETED':
        return <LottieStatusIcon animationData={success} />;
      default:
        return null;
    }
  };

  return (
    <InstructionsContainer className={className}>
      <InstructionText $status={stepStatus}>{getInstructionText()}</InstructionText>
      {getIcon()}
    </InstructionsContainer>
  );
};
